import { type ActionCreator, on, type ReducerTypes } from '@ngrx/store';
import { produce } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import {
  createAliasForAggregation,
  isDefaultAggregationAlias,
} from '@blaze-shared/elements-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';
import {
  type AggregateWorkflowNode,
  type ComputeWorkflowNode,
  findFirstNodeInWorkflowOfType,
  isAggregateNode,
  isAggregateNodeWithChartPrefix,
  isComputeNode,
  isNodeIdWithChartsComputePrefix,
} from '@blaze-shared/workflow-domain';

import { isVariantOfChart } from '../../canvas-elements/element-model/element-type-guards';
import { type ChartElement } from '../../elements';
import { elementDataSourceChangeTrigger } from '../elements/elements.actions';

export const chartReducerTypes: ReducerTypes<EngineStateBase, ActionCreator[]>[] = [
  on(elementDataSourceChangeTrigger, (state, { workflowId, workflow }) => {
    if (!workflow) {
      return state;
    }

    const elementId = state.connections.find(
      (c) =>
        c.source.layerType === 'Workflow' &&
        c.source.id === workflowId &&
        c.target.layerType === 'Ui',
    )?.target.id;
    if (!elementId) {
      return state;
    }

    const element = state.entities[elementId];

    if (!isVariantOfChart(element)) {
      return state;
    }

    const data: Exclude<ChartElement['props']['content']['data'], undefined> = {};

    const computeNodes = workflow.nodes.filter(
      (node): node is ComputeWorkflowNode =>
        isNodeIdWithChartsComputePrefix(node) && isComputeNode(node),
    );
    const aggregateNode = findFirstNodeInWorkflowOfType(
      workflow,
      (node): node is AggregateWorkflowNode =>
        isAggregateNodeWithChartPrefix(node) && isAggregateNode(node),
    );

    // if AGGREGATE NODE formula change => update aggregation
    aggregateNode?.functionDefs.forEach((fDef, index) => {
      if (index === 0) {
        if (aggregateNode && fDef.name) {
          const alias = fDef.alias ?? undefined;
          data.aggregation = fDef.name;
          data.alias = alias;
        } else if (!aggregateNode) {
          data.aggregation = undefined;
        }
        if (!data.multiSeries) {
          const alias = isDefaultAggregationAlias(fDef.alias)
            ? createAliasForAggregation({ alias: undefined }, 0, undefined, undefined)
            : fDef.alias;
          data.multiSeries = [{ alias }];
        }
      } else {
        if (index > 0) {
          data.multiSeries?.push({
            aggregation: fDef.name,
            mainValue: fDef.column,
            alias: fDef.alias,
          });
        }
      }
    });

    // if !AGGREGATE NODE do nothing
    if (!aggregateNode) {
      return state;
    }

    // if AGGREGATE NODE column change => update mainValue
    if (aggregateNode && aggregateNode.functionDefs[0].column) {
      data.mainValue = aggregateNode.functionDefs[0].column;
    } else if (!aggregateNode) {
      data.mainValue = undefined;
    }
    // if AGGREGATE NODE groupBy change => update groupBy
    // if AGGREGATE NODE groupBy[0].contains computeBy => groupBy split by date

    const isCompteByDetails = aggregateNode.groupByColumns.includes('computeByDetails');
    const isComputeByDay =
      aggregateNode.groupByColumns.includes('computeByDetails') &&
      computeNodes.find((node) => node.name === 'computeByDetails')?.formula?.includes('DAY');
    const isComputeByQuarter =
      aggregateNode.groupByColumns.includes('computeByDetails') &&
      computeNodes.find((node) => node.name === 'computeByDetails')?.formula?.includes('QUARTER');
    const isComputeByYear = aggregateNode.groupByColumns.includes('computeByYear');
    const isComputeByWeekStartDate =
      aggregateNode.groupByColumns.includes('computeByDetails') &&
      computeNodes
        .find((node) => node.name === 'computeByDetails')
        ?.formula?.includes('WEEKSTARTDATE');
    const isComputeByMonth =
      aggregateNode.groupByColumns.includes('computeByMonth') ||
      (aggregateNode.groupByColumns.includes('computeByDetails') &&
        computeNodes.find((node) => node.name === 'computeByDetails')?.formula?.includes('MONTH'));

    if (aggregateNode.groupByColumns[0]) {
      if (aggregateNode.groupByColumns[0].includes('computeBy')) {
        //  get groupBy from computeByYear
        const computeByColumnKey = computeNodes
          .find((node) => node.name === 'computeByYear')
          ?.formula?.match(/"([^"]+)"/)?.[1];
        data.groupBy = computeByColumnKey;

        //  if groupBy includes computeByMonth & computeByDetails => groupByDetails Daily
        if (isComputeByMonth && isComputeByDay) {
          data.groupByDetails = 'daily';
          //  if groupBy includes computeByYear & computeByDetails & FORMULA includes WEEKSTARTDATE => groupByDetails Weekly
        } else if (isComputeByYear && isCompteByDetails && isComputeByWeekStartDate) {
          data.groupByDetails = 'weekly';
          //  if groupBy includes computeByYear & computeByDetails & FORMULA includes MONTH => groupByDetails Monthly
        } else if (isComputeByYear && isComputeByMonth) {
          data.groupByDetails = 'monthly';
          //  if groupBy includes computeByYear & computeByDetails & FORMULA includes QUARTER => groupByDetails Quarterly
        } else if (isComputeByYear && isComputeByQuarter) {
          data.groupByDetails = 'quarterly';
        } else if (isComputeByYear) {
          data.groupByDetails = 'yearly';
        } else {
          data.groupByDetails = undefined;
        }
      } else {
        data.groupBy = aggregateNode.groupByColumns[0];
        data.groupByDetails = undefined;
      }
    }
    if (!aggregateNode.groupByColumns[0]?.includes('computeBy')) {
      if (isComputeByYear) {
        //  get segmentData from computeByYear
        const computeByColumnKey = computeNodes
          .find((node) => node.name === 'computeByYear')
          ?.formula?.match(/"([^"]+)"/)?.[1];
        data.segmentData = computeByColumnKey;

        //  if segmentData includes computeByMonth & computeByDetails => segmentDataDetails Daily
        if (isComputeByMonth && isComputeByDay) {
          data.segmentDataDetails = 'daily';
          //  if segmentData includes computeByYear & computeByDetails & FORMULA includes WEEKSTARTDATE => segmentDataDetails Weekly
        } else if (isComputeByYear && isCompteByDetails && isComputeByWeekStartDate) {
          data.segmentDataDetails = 'weekly';
          //  if segmentData includes computeByYear & computeByDetails & FORMULA includes MONTH => segmentDataDetails Monthly
        } else if (isComputeByYear && isComputeByMonth) {
          data.segmentDataDetails = 'monthly';
          //  if segmentData includes computeByYear & computeByDetails & FORMULA includes QUARTER => segmentDataDetails Quarterly
        } else if (isComputeByYear && isComputeByQuarter) {
          data.segmentDataDetails = 'quarterly';
        } else if (isComputeByYear) {
          data.segmentDataDetails = 'yearly';
        } else {
          data.segmentDataDetails = undefined;
        }
      } else if (aggregateNode.groupByColumns[1]) {
        data.segmentData = aggregateNode.groupByColumns[1];
        data.segmentDataDetails = undefined;
      }
    }

    return produce(state, (draft) => {
      const element = draft.entities[elementId];
      if (!isVariantOfChart(element)) {
        return;
      }
      if (isDeepEqual(element.props.content?.data, data)) {
        return;
      }

      console.log('[Blaze charts] Updating chart data', elementId, data);
      element.props = applyPropsPatch(element.props, { content: { data } }) as typeof element.props;
    });
  }),
];
